import useAPI from "@toothfairy/shared-api/useApi";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppDateUtility from "@toothfairy/shared-ui/AppDateUtility";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import dayjs from "dayjs";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import API_Consumption from "../../API/API_Consumption";
import trainingAPI from "../../API/Training/trainingAPI";
import chatsHelper from "../../API/Chats/chatsHelper";
import agentFunctionsAPI from "../../API/AgentFunctions";
import execEnvsHelper from "../../API/ExecutionEnvironments/execEnvsHelper";
import authorisationsHelper from "../../API/Authorisations/authorisationsHelper";
import jobsHelper from "../../API/Jobs/jobsHelper";
import triggersHelper from "../../API/Triggers/triggersHelper";
import requestLogsHelper from "../../API/RequestLogs/requestLogsHelper";
import usePath from "../../Hooks/usePath";
import useWorkspaces from "../../Hooks/useWorkspaces";
import DashboardScreen from "./DashboardScreen";
import { RequestType, TrainingLogType } from "../../models";
import Constants from "../../Constants";
import {
  getBillingPeriodParams,
  getTotalUoiFromCosts,
} from "../../Constants/billing";

const DashboardScreenContainer = () => {
  const [labels, setLabels] = useState([]);
  const [datasets, setDatasets] = useState();
  const { params } = usePath();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const {
    getLastWorkspaceModelMeta,
    getLastWorkspaceReadingComprehensionMeta,
    isNLPModelTrained,
    isRCModelTrained,
    isGNModelTrained,
    getActiveWorkspace,
    getActiveTrainingLog,
    isUserAdmin,
  } = useWorkspaces();
  const isAdmin = isUserAdmin();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const startDate = `${selectedDate.get("year")}-${selectedDate.format(
    "MM",
  )}-01`;
  const endDate = `${selectedDate.get("year")}-${selectedDate.format(
    "MM",
  )}-${selectedDate.daysInMonth()}`;

  const activeWorkspace = getActiveWorkspace();
  const billingPeriod = useMemo(
    () => getBillingPeriodParams(activeWorkspace, selectedDate),
    [
      activeWorkspace?.billingPeriodStart,
      activeWorkspace?.billingPeriodEnd,
      selectedDate?.valueOf(),
    ],
  );

  const [nersBreakDown, setNersBreakDown] = useState();
  const [intentsBreakDown, setIntentsBreakDown] = useState();
  const [topicsBreakDown, setTopicsBreakDown] = useState();
  const [documentsBreakdown, setDocumentsBreakdown] = useState([0, 0]);

  const {
    data: costData,
    error: costAPIError,
    loading: costAPIInProgress,
    apiRequest: fetchCost,
    lastUpdate: lastcostDataUpdate,
  } = useAPI(API_Consumption.getCosts);

  const {
    data: trainingData,
    error: trainingAPIError,
    loading: trainingAPIInProgress,
    apiRequest: fetchTraining,
    lastUpdate: lastTrainingDataUpdate,
    optimisticDataUpdate,
  } = useAPI(trainingAPI.getDocumentsByWorkspace);
  const [normalisedConsumption, setNormalisedConsumption] = useState();
  const [
    normalisedTrainingConsumption,
    setNormalisedTrainingConsumption,
  ] = useState();
  const [totalTrainingCost, setTotalTrainingCost] = useState(0);
  const [totalPredictionCost, setTotalPredictionCost] = useState(0);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [mergedCostData, setMergedCostData] = useState(null);

  // Child workspaces now come from the merged monthCosts payload, which the
  // backend aggregates server-side (root + all direct children). This means
  // admin-created children the viewer has no token for are still represented,
  // and consumption (including TF Code) is attributed to the workspace it came
  // from. Falls back to the workspace list while the first fetch is in flight.
  const childWorkspaces = useMemo(
    () =>
      (mergedCostData?.workspaces || [])
        .filter((ws) => !ws?.isRoot)
        .map((ws) => ({ id: ws.id, title: ws.title })),
    [mergedCostData?.workspaces],
  );

  const fetchFamilyCosts = useCallback(
    async (params) => {
      setPaginationLoading(true);
      setMergedCostData(null);
      try {
        const response = await API_Consumption.getCosts({
          ...params,
          includeChildren: true,
          excludePerMethodperDay: true,
        });
        const data = response?.data || response;
        if (data) setMergedCostData(data);
      } catch (error) {
        console.error("Error fetching family costs:", error);
      } finally {
        setPaginationLoading(false);
      }
    },
    [],
  );

  // Chat Analytics state
  const {
    data: chatAnalyticsData,
    error: chatAnalyticsError,
    loading: chatAnalyticsAPIInProgress,
    apiRequest: fetchChatAnalytics,
    lastUpdate: lastChatAnalyticsUpdate,
    response: chatAnalyticsResponse,
  } = useAPI(chatsHelper.getChatAnalytics);

  // Dashboard filter state - default to last 6 months
  const [filterStartDate, setFilterStartDate] = useState(() =>
    dayjs().subtract(6, "month").toDate(),
  );
  const [filterEndDate, setFilterEndDate] = useState(() => new Date());
  const [filterAgentId, setFilterAgentId] = useState("all");
  const [rawChatsData, setRawChatsData] = useState([]);

  // Agent Functions state
  const {
    data: agentFunctionsData,
    error: agentFunctionsError,
    loading: agentFunctionsAPIInProgress,
    apiRequest: fetchAgentFunctions,
    lastUpdate: lastAgentFunctionsUpdate,
  } = useAPI(agentFunctionsAPI.getAgentFunctionsByWorkspaceID);

  useEffect(() => {
    const dataToUse = mergedCostData || costData;
    if (dataToUse?.apiUsage) {
      const normalisedData = dataToUse?.apiUsage?.perDay;

      const normalisedArray = [];
      if (normalisedData?.length > 0)
        for (let i = 0; i < normalisedData.length; i++) {
          const couple = normalisedData[i];
          const isBillingPeriod = !!billingPeriod;
          normalisedArray.push({
            date: isBillingPeriod
              ? dayjs(couple.date).format("DD MMM")
              : dayjs(couple.date).format("D"),
            consumption: couple.requests,
          });
        }

      if (billingPeriod) {
        setLabels(normalisedArray?.map((u) => u.date));
      } else {
        setLabels(
          AppDateUtility.getDaysInMonth(
            selectedDate.get("month") + 1,
            selectedDate.get("year"),
          ),
        );
      }

      setDatasets(normalisedArray?.map((u) => u.consumption));
    }
  }, [mergedCostData, lastcostDataUpdate, state?.activeWorkspaceID, billingPeriod]);

  const fetchNers = () => {
    if (getActiveTrainingLog("nlp")?.trainingMeta?.meta?.ners) {
      const data = getActiveTrainingLog("nlp")?.trainingMeta?.meta?.ners;
      const enrichedNerData = [];
      for (let i = 0; i < data?.length; i++) {
        const ner = data[i];
        const matchingNER = state?.aiEntitiesConfig?.find(
          (u) => u.label === ner.name,
        );
        const _color =
          matchingNER?.backgroundColor || AppUtilities.randomRGBA();
        enrichedNerData.push({
          ...ner,
          color: _color,
          legendFontColor: _color,
          legendFontFamily: "normalFont",
        });
        ner.color = _color;
        ner.legendFontColor = _color;
      }
      setNersBreakDown(enrichedNerData);
    }
  };
  const fetchIntents = () => {
    if (getActiveTrainingLog("nlp")?.trainingMeta?.meta?.intents) {
      const data = getActiveTrainingLog("nlp")?.trainingMeta?.meta?.intents;
      const enrichedIntentsData = [];
      for (let i = 0; i < data?.length; i++) {
        const ner = data[i];
        const matchingNER = state?.aiEntitiesConfig?.find(
          (u) => u.label === ner.name,
        );
        const _color =
          matchingNER?.backgroundColor || AppUtilities.randomRGBA();
        enrichedIntentsData.push({
          ...ner,
          color: _color,
          legendFontColor: _color,
          legendFontFamily: "normalFont",
        });
        ner.color = _color;
        ner.legendFontColor = _color;
      }
      setIntentsBreakDown(enrichedIntentsData);
    }
  };
  const fetchTopics = async (aiEntitiesConfig) => {
    const result = await fetchTraining({
      workspaceID: getActiveWorkspace()?.id,
      countOnly: false,
      fields: ["topics"],
    });
    if (result) {
      const raw_topics = result?.data?.map((u) => u?.topics)?.flat();
      // from the array above generate a new array with the count of each topic and the id
      const topicsCount = raw_topics?.reduce((acc, curr) => {
        if (acc[curr]) {
          acc[curr] += 1;
        } else {
          acc[curr] = 1;
        }
        return acc;
      }, {});
      const topics = Object.keys(topicsCount).map((u) => ({
        id: u,
        count: topicsCount[u],
      }));
      const enrichedTopicsData = [];
      for (let i = 0; i < topics?.length; i++) {
        const topic = topics[i];
        const matchingTopic = aiEntitiesConfig?.find((u) => u.id === topic?.id);
        if (!matchingTopic) continue;
        const _color =
          matchingTopic?.backgroundColor || AppUtilities.randomRGBA();
        enrichedTopicsData.push({
          name: matchingTopic?.label || "Undefined",
          color: _color,
          legendFontColor: _color,
          legendFontFamily: "normalFont",
          count: topic.count,
        });
        topic.color = _color;
        topic.legendFontColor = _color;
      }
      setTopicsBreakDown(enrichedTopicsData);
    }
  };
  const fetchDocumentsByType = async (
    isNLPModelTrained,
    isRCModelTrained,
    isGNModelTrained,
  ) => {
    const result = await Promise.all([
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "nlpTraining",
        pageLimit: 5000,
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "readComprehensionTraining",
        pageLimit: 5000,
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "readComprehensionContent",
        pageLimit: 5000,
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "generativeTraining",
        pageLimit: 5000,
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "questionAnswering",
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "readComprehensionPdf",
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "readComprehensionFile",
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "readComprehensionUrl",
        pageLimit: 5000,
      }),
      fetchTraining({
        workspaceID: state.activeWorkspaceID,
        countOnly: true,
        type: "conversationalTraining",
        pageLimit: 5000,
      }),
    ]);

    setDocumentsBreakdown([
      isNLPModelTrained ? result[0].totalResults : 0,
      isRCModelTrained ? result[1].totalResults : 0,
      result[2].totalResults,
      isGNModelTrained ? result[3].totalResults : 0,
      result[4].totalResults,
      result[5].totalResults,
      result[6].totalResults,
      result[7].totalResults,
      result[8].totalResults,
    ]);
  };
  useEffect(() => {
    if (activeWorkspace?.workspaceToken) {
      fetchFamilyCosts({
        workspaceId: activeWorkspace?.id,
        ...(billingPeriod || {
          month: selectedDate?.get("month") + 1,
          year: selectedDate?.get("year"),
        }),
        token: activeWorkspace?.workspaceToken,
      });
    }
  }, [
    JSON.stringify(selectedDate),
    state?.activeWorkspaceID,
    activeWorkspace?.workspaceToken,
    billingPeriod?.billingPeriodStart,
    billingPeriod?.billingPeriodEnd,
  ]);

  useEffect(() => {
    if (state?.activeWorkspaceID)
      fetchDocumentsByType(
        isNLPModelTrained(),
        isRCModelTrained(),
        isGNModelTrained(),
      );
  }, [
    JSON.stringify(selectedDate),
    state?.activeWorkspaceID,
    isNLPModelTrained(),
    isRCModelTrained(),
  ]);

  useEffect(() => {
    if (state?.activeWorkspaceID && isNLPModelTrained()) {
      fetchNers();
      fetchIntents();
    }
  }, [
    state?.activeWorkspaceID,
    isNLPModelTrained(),
    getActiveTrainingLog("nlp")?.trainingMeta,
    selectedDate,
  ]);

  useEffect(() => {
    if (
      getActiveWorkspace()?.id &&
      state?.aiEntitiesConfig?.length > 0 &&
      documentsBreakdown?.length > 0
    ) {
      fetchTopics([...state?.aiEntitiesConfig]);
    }
  }, [
    getActiveWorkspace()?.id,
    state?.aiEntitiesConfig?.length,
    documentsBreakdown?.length,
  ]);

  // Fetch chat analytics when workspace or filters change
  useEffect(() => {
    if (state?.activeWorkspaceID) {
      fetchChatAnalytics(state.activeWorkspaceID, 5000, {
        startDate: filterStartDate,
        endDate: filterEndDate,
        agentId: filterAgentId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.activeWorkspaceID, filterStartDate, filterEndDate, filterAgentId]);

  // Update rawChatsData when response changes
  useEffect(() => {
    if (chatAnalyticsResponse?.rawChats) {
      setRawChatsData(chatAnalyticsResponse.rawChats);
    }
  }, [chatAnalyticsResponse]);

  // Fetch agent functions when workspace changes
  useEffect(() => {
    if (state?.activeWorkspaceID) {
      fetchAgentFunctions({ workspaceID: state.activeWorkspaceID });
    }
  }, [state?.activeWorkspaceID]);

  // Dispatch agent functions to state when fetched
  useEffect(() => {
    const functions =
      agentFunctionsData?.data?.functionsByWorkspace?.items ||
      agentFunctionsData?.functionsByWorkspace?.items ||
      [];
    if (functions.length > 0) {
      const cleanFunctions = functions
        .filter((fn) => !fn?._deleted)
        .map((fn) => ({ ...fn }));
      dispatch("SET_AGENTS_FUNCTIONS", cleanFunctions);
    }
  }, [agentFunctionsData]);

  // Fetch execution environments (hooks) when workspace changes
  useEffect(() => {
    const loadExecEnvs = async () => {
      if (!state?.activeWorkspaceID) return;
      try {
        const execEnvsData =
          await execEnvsHelper.getCustomCodeExecutionEnvironments(
            state.activeWorkspaceID,
            null,
          );
        let execEnvs =
          execEnvsData?.data?.customCodeExecutionEnvironmentsByWorkspace
            ?.items;
        execEnvs =
          execEnvs
            ?.map((u) => {
              if (u?._deleted !== true)
                return {
                  ...u,
                  customExecutionSecrets:
                    typeof u?.customExecutionSecrets === "string"
                      ? (() => {
                          try {
                            return JSON.parse(u?.customExecutionSecrets);
                          } catch {
                            return [];
                          }
                        })()
                      : u?.customExecutionSecrets,
                };
            })
            ?.filter((u) => u != null) || [];
        dispatch("SET_EXEC_ENVS", execEnvs || []);
      } catch (error) {
        console.error("Failed to fetch exec envs for dashboard:", error);
      }
    };
    loadExecEnvs();
  }, [state?.activeWorkspaceID]);

  // Fetch authorisations when workspace changes
  useEffect(() => {
    const loadAuthorisations = async () => {
      if (!state?.activeWorkspaceID) return;
      try {
        const authorisationsData =
          await authorisationsHelper.getAuthorisations(
            state.activeWorkspaceID,
            null,
          );
        let authorisations =
          authorisationsData?.data?.authorisationsByWorkspace?.items;
        authorisations =
          authorisations
            ?.map((u) => {
              if (u?._deleted !== true)
                return {
                  ...u,
                  staticArgs: (() => {
                    try {
                      return u?.staticArgs
                        ? JSON.parse(u?.staticArgs)
                        : null;
                    } catch {
                      return null;
                    }
                  })(),
                };
            })
            ?.filter((u) => u != null) || [];
        dispatch("SET_AUTHORISATIONS", authorisations || []);
      } catch (error) {
        console.error("Failed to fetch authorisations for dashboard:", error);
      }
    };
    loadAuthorisations();
  }, [state?.activeWorkspaceID]);

  // Fetch scheduled jobs for the active workspace so the bird's-eye graph can
  // link agents -> jobs. Mirrors JobsList.loadJobs (cronJobsByWorkspace).
  useEffect(() => {
    const loadJobs = async () => {
      if (!state?.activeWorkspaceID) return;
      try {
        let resultJobs = await jobsHelper.getJobsByWorkspaceID(
          state.activeWorkspaceID,
        );
        resultJobs = resultJobs?.data?.cronJobsByWorkspace?.items?.filter(
          (u) => u?._deleted !== true,
        );
        dispatch("SET_JOBS", resultJobs || []);
      } catch (error) {
        console.error("Failed to fetch jobs for dashboard:", error);
      }
    };
    loadJobs();
  }, [state?.activeWorkspaceID]);

  // Fetch triggers for the active workspace so the bird's-eye graph can link
  // agents -> triggers. Mirrors TriggersList.loadTriggers.
  useEffect(() => {
    const loadTriggers = async () => {
      if (!state?.activeWorkspaceID) return;
      try {
        let resultTriggers =
          await triggersHelper.getTriggersByWorkspaceID(state.activeWorkspaceID);
        resultTriggers = resultTriggers?.data?.triggersByWorkspace?.items?.filter(
          (u) => u?._deleted !== true,
        );
        dispatch("SET_TRIGGERS", resultTriggers || []);
      } catch (error) {
        console.error("Failed to fetch triggers for dashboard:", error);
      }
    };
    loadTriggers();
  }, [state?.activeWorkspaceID]);

  // Compute agents by mode breakdown
  const agentsByMode = React.useMemo(() => {
    if (!state?.agents || state.agents.length === 0) return null;

    const breakdown = {
      speed: 0,
      accuracy: 0,
      custom: 0,
      retriever: 0,
      coder: 0,
      mathematician: 0,
      chatter: 0,
      planner: 0,
      computer: 0,
      voice: 0,
      unknown: 0,
      total: 0,
    };

    state.agents.forEach((agent) => {
      const mode = agent?.mode?.toLowerCase() || "unknown";
      if (breakdown.hasOwnProperty(mode)) {
        breakdown[mode]++;
      } else {
        breakdown.unknown++;
      }
      breakdown.total++;
    });

    return breakdown;
  }, [state?.agents]);

  // Compute agent functions by type breakdown
  const agentFunctionsByType = React.useMemo(() => {
    // Data can be at agentFunctionsData.data.functionsByWorkspace.items or agentFunctionsData.functionsByWorkspace.items
    const functions =
      agentFunctionsData?.data?.functionsByWorkspace?.items ||
      agentFunctionsData?.functionsByWorkspace?.items ||
      [];
    if (functions.length === 0) return null;

    const breakdown = {
      api: 0,
      mcp: 0,
      callback: 0,
      database: 0,
      html: 0,
      chat: 0,
      handoff: 0,
      code: 0,
      total: 0,
    };

    functions.forEach((fn) => {
      if (fn?._deleted) return;

      breakdown.total++;

      if (fn?.isMCPServer) {
        breakdown.mcp++;
      } else if (fn?.isCallbackFn) {
        breakdown.callback++;
      } else if (fn?.isDatabaseScript) {
        breakdown.database++;
      } else if (fn?.isHtmlFn) {
        breakdown.html++;
      } else if (fn?.isChatFn) {
        breakdown.chat++;
      } else if (fn?.isHandOffFn) {
        breakdown.handoff++;
      } else if (fn?.isCodeTemplate) {
        breakdown.code++;
      } else {
        breakdown.api++;
      }
    });

    return breakdown;
  }, [agentFunctionsData]);

  const dataForCostChart = mergedCostData || costData;
  const coreTrainingCost = dataForCostChart?.trainingUsage?.trainingOverview;

  // Full-fidelity request logs for the billing period — the SINGLE source for
  // both the per-user card and the UoI-by-model breakdown. The billing
  // monthCosts payload can't feed the model split: the Dashboard fetch passes
  // excludePerMethodperDay, which makes the backend AGGREGATE perDayAndType and
  // DROP modelType from every item (everything buckets as "unspecified"). Raw
  // RequestLogs keep modelType per record, so we read them once here via the
  // same requestsByMonthAndWorkspace index the billing lambda uses
  // (srtKey = `${workspaceID}-MM-yy`) and share the result downstream.
  const getMonthKeysForPeriod = (bp, sd) => {
    const fmt = "MM-YY";
    if (bp?.billingPeriodStart && bp?.billingPeriodEnd) {
      const start = dayjs(bp.billingPeriodStart).startOf("day");
      const end = dayjs(bp.billingPeriodEnd).endOf("day");
      if (start.isValid() && end.isValid()) {
        const keys = [];
        let cursor = start;
        while (cursor.isBefore(end) || cursor.isSame(end, "month")) {
          keys.push(cursor.format(fmt));
          cursor = cursor.add(1, "month");
        }
        return keys;
      }
    }
    return [sd.format(fmt)];
  };

  const [consumptionLogs, setConsumptionLogs] = useState([]);
  const [consumptionLogsLoading, setConsumptionLogsLoading] = useState(false);
  const consumptionMonthKeys = useMemo(
    () => getMonthKeysForPeriod(billingPeriod, selectedDate),
    [
      billingPeriod?.billingPeriodStart,
      billingPeriod?.billingPeriodEnd,
      selectedDate?.valueOf(),
    ],
  );
  useEffect(() => {
    if (!activeWorkspace?.id) return;
    let cancelled = false;
    setConsumptionLogsLoading(true);
    requestLogsHelper
      .getRequestLogsByWorkspaceAndMonths(
        activeWorkspace.id,
        consumptionMonthKeys,
      )
      .then((result) => {
        if (cancelled) return;
        const start = billingPeriod?.billingPeriodStart
          ? dayjs(billingPeriod.billingPeriodStart).startOf("day")
          : null;
        const end = billingPeriod?.billingPeriodEnd
          ? dayjs(billingPeriod.billingPeriodEnd).endOf("day")
          : null;
        const inPeriod = (log) => {
          if (!start && !end) return true;
          const d = dayjs(
            log?.createdAt ||
              (log?.creationTime ? log.creationTime * 1000 : null),
          );
          if (!d.isValid()) return true;
          if (start && d.isBefore(start)) return false;
          if (end && d.isAfter(end)) return false;
          return true;
        };
        setConsumptionLogs(
          (result || []).filter(
            (u) =>
              u?.status === "success" &&
              u?.isFromToothFairy !== false &&
              inPeriod(u),
          ),
        );
      })
      .catch((error) =>
        console.error("[Dashboard] failed to load consumption logs:", error),
      )
      .finally(() => {
        if (!cancelled) setConsumptionLogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeWorkspace?.id,
    consumptionMonthKeys?.join(","),
    billingPeriod?.billingPeriodStart,
    billingPeriod?.billingPeriodEnd,
  ]);

  // UoI per model size, from the raw logs (modelType survives here).
  const FIXED_METHOD_COSTS = {
    searcher: 0.01,
    tokenizer: 0.001,
    embedder: 0.001,
  };
  const consumptionByModel = useMemo(() => {
    const byModel = {};
    consumptionLogs.forEach((log) => {
      const costUSD =
        log?.totalCostUSD && log.totalCostUSD > 0
          ? log.totalCostUSD
          : FIXED_METHOD_COSTS[log?.type] || 0;
      const uoi = costUSD * 100;
      const m = log?.modelType || "unspecified";
      byModel[m] = (byModel[m] || 0) + uoi;
    });
    return Object.keys(byModel).length > 0 ? byModel : null;
  }, [consumptionLogs]);

  let _parsingCost = [];
  let _parsingLabels = [];
  let _trainingCost = [];
  let _trainingLabels = [];
  if (dataForCostChart?.apiUsage?.costsOverview) {
    _parsingCost = Object.values(dataForCostChart?.apiUsage?.costsOverview).map(
      (u) => u?.charge || 0,
    );
    _parsingLabels = Object.keys(dataForCostChart?.apiUsage?.costsOverview);
  }
  if (coreTrainingCost) {
    _trainingCost = Object.values(coreTrainingCost).map((u) => u?.charge || 0);
    _trainingLabels = Object.keys(coreTrainingCost);
  }
  const costChart = {
    _parsingLabels,
    _trainingLabels,
    _parsingCost,
    _trainingCost,
  };
  useEffect(() => {
    const dataToUse = mergedCostData || costData;
    if (dataToUse?.apiUsage?.perDayAndType) {
      setNormalisedConsumption(
        normalizeData(dataToUse?.apiUsage?.perDayAndType),
      );
      let totalCost = Object.values(dataToUse?.apiUsage?.costsOverview)?.reduce(
        (acc, curr) => acc + curr?.charge,
        0,
      );
      totalCost = parseFloat(totalCost.toFixed(2));
      setTotalPredictionCost(totalCost);
    }
    if (dataToUse?.trainingUsage?.perDayAndType) {
      setNormalisedTrainingConsumption(
        normalizeTrainingData(dataToUse?.trainingUsage?.perDayAndType),
      );
      let totalCostForTraining = Object.values(
        dataToUse?.trainingUsage?.trainingOverview,
      )?.reduce((acc, curr) => acc + curr?.charge, 0);
      totalCostForTraining = parseFloat(totalCostForTraining.toFixed(2));
      setTotalTrainingCost(totalCostForTraining);
    }
  }, [mergedCostData, costData?.apiUsage?.perDayAndType]);

  function normalizeData(inputData) {
    const agentIds = state?.agents?.map((agent) => agent?.id) || [];
    const childWsIds = childWorkspaces.map((ws) => `ws:${ws.id}`);
    const keys = [...agentIds, ...childWsIds, "tf_main", "tfcode", "auto_finetuner"];
    const dataByAgent = keys.reduce((acc, key) => ({ ...acc, [key]: {} }), {});
    const dates = Object.keys(inputData).sort();

    dates.forEach((date) => {
      Object.entries(inputData[date]).forEach(([method, methodData]) => {
        if (Array.isArray(methodData)) {
          methodData.forEach((item) => {
            // Attribute consumption to the workspace it came from. The backend
            // tags every perDayAndType item with workspaceID; items from a child
            // workspace (including TF Code usage) are bucketed under ws:<id>
            // instead of being lumped under the root agent. Root items keep their
            // agent id, with all tf_main_* variants consolidated into "tf_main".
            const rootWorkspaceId = activeWorkspace?.id;
            const rawAgentID = item.agentID || "unknown";
            const agentID =
              item.workspaceID && item.workspaceID !== rootWorkspaceId
                ? `ws:${item.workspaceID}`
                : rawAgentID.startsWith("tf_main")
                ? "tf_main"
                : rawAgentID;
            if (dataByAgent[agentID] && !dataByAgent[agentID][date]) {
              dataByAgent[agentID][date] = { uoi: 0 };
            } else if (dataByAgent[agentID] == undefined) {
              dataByAgent[agentID] = { [date]: { uoi: 0 } };
            }
            dataByAgent[agentID][date].uoi +=
              item.uoi || 0;
          });
        }
      });
    });

    keys.forEach((agentID) => {
      if (!dataByAgent[agentID]) {
        dataByAgent[agentID] = {};
      }
      dates.forEach((date) => {
        if (!dataByAgent[agentID][date]) {
          dataByAgent[agentID][date] = { uoi: 0 };
        }
      });
    });

    const datasets = keys.map((agentID) => ({
      label: agentID,
      data: dates.map((date) => dataByAgent[agentID][date]?.uoi || 0),
    }));

    const formattedLabels = dates.map((date) => {
      return billingPeriod ? dayjs(date).format("DD MMM") : dayjs(date).format("D");
    });

    return {
      labels: formattedLabels,
      datasets: datasets,
    };
  }

  function normalizeTrainingData(inputData) {
    const modelNames = new Set();
    const dateData = {};

    // First pass: collect all unique model names and organize data by date
    Object.entries(inputData).forEach(([dateStr, data]) => {
      const dateOnlyStr = dateStr.split("T")[0];
      if (!dateData[dateOnlyStr]) {
        dateData[dateOnlyStr] = {};
      }

      Object.entries(data).forEach(([modelName, modelData]) => {
        modelNames.add(modelName);
        if (modelData && typeof modelData.duration === "number") {
          dateData[dateOnlyStr][modelName] = modelData.duration; // Convert to minutes
        }
      });
    });

    const sortedDates = Object.keys(dateData).sort();
    const sortedModelNames = Array.from(modelNames).sort();

    // Initialize datasets
    const datasets = sortedModelNames.map((modelName) => ({
      label: (state?.trainableModels || []).find((u) => u?.id == modelName)?.label || modelName,
      data: [],
    }));

    // Fill in the data
    sortedDates.forEach((date) => {
      sortedModelNames.forEach((modelName, index) => {
        datasets[index].data.push(dateData[date][modelName] || 0);
      });
    });

    // Format labels (dates)
    const formattedLabels = sortedDates.map((date) => {
      return billingPeriod ? dayjs(date).format("DD MMM") : dayjs(date).format("DD/MM");
    });

    return {
      labels: formattedLabels,
      datasets: datasets,
    };
  }

  // Export chat data to CSV
  const handleExportChats = useCallback(() => {
    if (!rawChatsData || rawChatsData.length === 0) return;

    const headers = [
      "Chat ID",
      "Created At",
      "Agent ID",
      "Agent Name",
      "Visibility",
      "Request Completed",
      "Completion Score",
      "Customer Satisfaction",
      "Quality Score",
      "PII Detected",
      "PII Types",
      "PII Occurrences",
      "Toxicity Detected",
      "Toxicity Score",
      "Toxicity Categories",
      "Escalation Required",
      "Escalation Reason",
    ];

    const rows = rawChatsData
      .filter((chat) => !chat._deleted && chat.visibility !== "incognito")
      .map((chat) => {
        const agent = state?.agents?.find((a) => a.id === chat.agentID);
        return [
          chat.id,
          chat.createdAt,
          chat.agentID || "",
          chat.agentID?.startsWith("tf_main") ? "Fairy AI" : (agent?.label || ""),
          chat.visibility || "",
          chat.requestCompleted != null ? String(chat.requestCompleted) : "",
          chat.requestCompletionScore != null
            ? String(chat.requestCompletionScore)
            : "",
          chat.customerSatisfactionScore != null
            ? String(chat.customerSatisfactionScore)
            : "",
          chat.qualityScore || "",
          chat.piiDetected != null ? String(chat.piiDetected) : "",
          Array.isArray(chat.piiTypes) ? chat.piiTypes.join("; ") : "",
          chat.piiOccurrences != null ? String(chat.piiOccurrences) : "",
          chat.toxicityDetected != null ? String(chat.toxicityDetected) : "",
          chat.toxicityScore != null ? String(chat.toxicityScore) : "",
          Array.isArray(chat.toxicityCategories)
            ? chat.toxicityCategories.join("; ")
            : "",
          chat.escalationRequired != null
            ? String(chat.escalationRequired)
            : "",
          chat.escalationReason || "",
        ];
      });

    const escapeCSV = (value) => {
      if (value == null) return "";
      const str = String(value);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `chat_analytics_${dayjs().format("YYYY-MM-DD_HH-mm")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [rawChatsData, state?.agents]);

  return (
    <DashboardScreen
      isAdmin={isAdmin}
      initialTabFromRouting={params.sectionId}
      changeMonthByUnit={(int) =>
        setSelectedDate(selectedDate.add(int, "month"))
      }
      intentsCount={getActiveTrainingLog("nlp")?.trainingMeta?.totalntents}
      nersCount={getActiveTrainingLog("nlp")?.trainingMeta?.totalNers}
      normalisedConsumption={normalisedConsumption}
      normalisedTrainingConsumption={normalisedTrainingConsumption}
      // CHECK IF THIS IS CORRECT
      trainingDataCount={
        getActiveTrainingLog("nlp")?.trainingMeta?.intents &&
        JSON.parse(getActiveTrainingLog("nlp")?.trainingMeta?.intents)?.length >
          0
          ? JSON.parse(
              getActiveTrainingLog("nlp")?.trainingMeta?.intents,
            )?.reduce((a, b) => ({
              count: a.count + b.count,
            }))?.count
          : 0
      }
      displayedAgents={[
        ...state?.agents?.map((u) => {
          return { id: u?.id, label: u?.id?.startsWith("tf_main") ? "Fairy AI" : u?.label, mode: u?.mode };
        }),
        ...childWorkspaces.map((ws) => ({
          id: `ws:${ws.id}`,
          label: ws.title,
          mode: "workspace",
        })),
        {
          id: "tf_main",
          label: "Fairy AI",
          mode: "retriever",
        },
        {
          id: "auto_finetuner",
          label: "Auto Finetuner",
          mode: "chatter",
        },
        {
          id: "tfcode",
          label: "TF Code Prediction API",
          mode: "coder",
        },
      ]}
      costData={mergedCostData || costData}
      parsingCost={_parsingCost}
      trainingCost={_trainingCost}
      parsingLabels={_parsingLabels}
      costChart={costChart}
      trainingDuration={0}
      batchConsumption={(mergedCostData || costData)?.batchUsage?.daily?.map(
        (u) => u?.parsedSentencesCount,
      )}
      lastTrainingDate={AppDateUtility.ConvertDateToDateStringFormat(
        getActiveTrainingLog("nlp")?.trainingMeta?.lastTraining,
        "D MMM YY",
      )}
      intentsBreakDown={intentsBreakDown}
      nersBreakDown={nersBreakDown}
      topicsBreakDown={topicsBreakDown}
      consumptionAPIInProgress={paginationLoading || costAPIInProgress}
      costAPIInProgress={paginationLoading || costAPIInProgress}
      selectedDate={selectedDate}
      labels={labels}
      datasets={datasets}
      documentsBreakdown={documentsBreakdown}
      trainingAPIInProgress={trainingAPIInProgress}
      totalPredictionCost={totalPredictionCost}
      totalTrainingCost={totalTrainingCost}
      chatAnalyticsData={chatAnalyticsData?.data || chatAnalyticsData}
      chatAnalyticsAPIInProgress={chatAnalyticsAPIInProgress}
      agentsByMode={agentsByMode}
      agentFunctionsByType={agentFunctionsByType}
      filterStartDate={filterStartDate}
      filterEndDate={filterEndDate}
      filterAgentId={filterAgentId}
      onFilterStartDateChange={setFilterStartDate}
      onFilterEndDateChange={setFilterEndDate}
      onFilterAgentChange={setFilterAgentId}
      onExportChats={handleExportChats}
      rawChatsCount={
        rawChatsData?.filter((c) => !c._deleted && c.visibility !== "incognito")
          ?.length || 0
      }
      consumptionByType={(mergedCostData || costData)?.apiUsage?.perType}
      consumptionByModel={consumptionByModel}
      consumptionLogs={consumptionLogs}
      consumptionLogsLoading={consumptionLogsLoading}
      totalUoIConsumed={getTotalUoiFromCosts(mergedCostData || costData)}
      cacheUsage={(mergedCostData || costData)?.apiUsage?.cacheUsage}
      billingPeriod={billingPeriod}
      agents={state?.agents}
      agentsFunctions={state?.agentsFunctions}
      execEnvs={state?.execEnvs}
      authorisations={state?.authorisations}
      mrcDocuments={state?.mrcDocuments}
      aiEntitiesConfig={state?.aiEntitiesConfig}
      jobs={state?.jobs}
      triggers={state?.triggers}
    />
  );
};

export default DashboardScreenContainer;
